<template>
    <div class="filter-period">
        <div class="filter-label" @click="showOptions = !showOptions">
            <date-range-picker v-show="placeCreatedDate" ref="picker" opens="center" :locale-data="dateLocale" :singleDatePicker="false" :timePicker="false" :showWeekNumbers="false" :showDropdowns="false" :autoApply="false" v-model="dateRange" :ranges="ranges" @update="changed">
            </date-range-picker>
        </div>
    </div>
</template>
<style lang="less">
.filter-period .reportrange-text {
    padding: 0;
    background: transparent;
    border: none;
    line-height: 1em;
    height: 1em;
    color: #212529;
}

.filter-period .daterangepicker.show-ranges {
    min-width: 693px;

    @media (max-width: 693px) {
        width: 278px;
        min-width: 278px;
    }
}

.filter-period .calendars-container {
    min-width: 550px;

    @media (max-width: 1024px) {
        width: 278px;
        min-width: 278px;
    }
}

.filter-period .drp-selected {
    display: none !important;
}

.filter-period .drp-buttons .btn {
    font-weight: 400;
}

.daterangepicker {
    position: absolute;
    display: flex;
    flex-direction: column;
    margin: 20px !important;

    @media (max-width: 700px) {
        max-width: 300px !important;

        .calendars-container {
            display: flex;
            flex-direction: column;
        }
    }
}
</style>
<script>
import DateRangePicker from "vue2-daterange-picker";
import keviApi from "../../../keviApi";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import { placeStartOfDayISO, placeEndOfDayISO, isoToPlaceLocalDate } from "../../../helpers/datetime";
export default {
    name: "filter-period",
    props: {
        name: {
            type: String,
            default: "",
        },
        currentValue: {
            type: [Object, String, Number],
            default: null,
        }
    },
    data() {
        return {
            showOptions: false,
            valueType: "timeDiff",
            value: null,
            today: new Date(),
            dateRange: {
                startDate: new Date(),
                endDate: new Date(),
            },
            placeCreatedDate: null,
        };
    },
    components: { DateRangePicker },
    mounted() {
        window.setTimeout(() => {
            const tz =
                (this.$store.state.place && this.$store.state.place.timezone) ||
                undefined;
            let start = null;
            let end = null;
            if (this.currentValue === null) {
                start = new Date();
                start.setDate(start.getDate() - 30);
                end = new Date();
            } else {
                // Stored values are place-tz day-boundary instants; map them back
                // to the place-local calendar day so the picker (browser-local
                // days) doesn't drift forward on each refresh.
                start = this.currentValue.start ? isoToPlaceLocalDate(this.currentValue.start, tz) : null;
                end = this.currentValue.end ? isoToPlaceLocalDate(this.currentValue.end, tz) : null;
            }
            if (start && end) {
                this.dateRange.startDate = start;
                this.dateRange.endDate = end;
            }
            this.changed();
        }, 10);

        this.updatePlaceCreatedDate();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        updatePlaceCreatedDate() {
            let options = keviApi.getOptions(
                "place/" + this.$store.state.place.id + "/details",
                "GET",
            );
            this.$http(options).then((response) => {
                this.placeCreatedDate = response.body.oldest_order || response.body.created_at;
            });
        },
        openOptions() {
            this.showOptions = true;
        },
        getValue() {
            // Start/end of the selected calendar days in the place timezone,
            // as proper ISO instants (see helpers/datetime).
            const tz =
                (this.$store.state.place && this.$store.state.place.timezone) ||
                undefined;
            return {
                start: placeStartOfDayISO(this.dateRange.startDate, tz),
                end: placeEndOfDayISO(this.dateRange.endDate, tz),
            };
        },
        changed() {
            this.value = this.dateRange;
            this.$emit("changed", this.getValue());
        },
    },
    computed: {
        placeId() {
            return this.$store.state.place.id;
        },
        valueLabel() {
            if (this.valueType === "timeDiff") {
                switch (this.value) {
                    case 365:
                        return this.t('reportFilters-last_12_months');
                    case 90:
                        return this.t('reportFilters-last_3_months');
                    default:
                        return this.t('reportFilters-last_x_days', [this.value]);
                }
            }
            return this.value;
        },
        ranges() {
            let today = new Date();
            today.setHours(0, 0, 0, 0);
            let yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            let thisMonthStart = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
            );
            let thisMonthEnd = new Date(
                today.getFullYear(),
                today.getMonth() + 1,
                0,
            );

            let ranges = {};
            ranges[this.t('reportFilters-ranges-today')] = [today, today];
            ranges[this.t('reportFilters-ranges-yesterday')] = [yesterday, yesterday];
            ranges[this.t('reportFilters-ranges-this_month')] = [thisMonthStart, thisMonthEnd];
            ranges[this.t('reportFilters-ranges-this_year')] = [
                new Date(today.getFullYear(), 0, 1),
                new Date(today.getFullYear(), 11, 31),
            ];
            ranges[this.t('reportFilters-ranges-last_month')] = [
                new Date(today.getFullYear(), today.getMonth() - 1, 1),
                new Date(today.getFullYear(), today.getMonth(), 0),
            ];
            ranges[this.t('reportFilters-ranges-last_year')] = [
                new Date(today.getFullYear() - 1, 0, 1),
                new Date(today.getFullYear() - 1, 11, 31),
            ];

            if (this.placeCreatedDate) {
                ranges[this.t('reportFilters-ranges-all_period')] = [
                    new Date(this.placeCreatedDate.replace(/-/g, "/")),
                    today,
                ];
            }
            return ranges;
        },
        dateLocale() {
            const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((index) => {
                const label = this.t(`dates-full_months-${index}`);
                return label;
            });
            return {
                firstDay: 0,
                format: 'dd-mm-yyyy',
                daysOfWeek: [
                    this.t('inputs-datepicker-weekdays-sun'),
                    this.t('inputs-datepicker-weekdays-mon'),
                    this.t('inputs-datepicker-weekdays-tue'),
                    this.t('inputs-datepicker-weekdays-wed'),
                    this.t('inputs-datepicker-weekdays-thu'),
                    this.t('inputs-datepicker-weekdays-fri'),
                    this.t('inputs-datepicker-weekdays-sat'),
                ],
                monthNames: months,
                applyLabel: this.t('reportFilters-apply'),
                cancelLabel: this.t('reportFilters-cancel'),
            };
        },
    },
    watch: {
        placeId() {
            this.updatePlaceCreatedDate();
            this.loadData();
        },
        value() {
            this.changed();
        },
    },
};
</script>
